import { queries, USERNAME_PLACEHOLDER } from 'RestAPIs/rapini';
import { getEndOfDay, getStartOfDay } from 'utils/date';

import { PlannerTaskTab } from '../types';
import {
  LEAVE_UNREAD_TASK_KEYS,
  LEAVE_SEEN_TASK_KEYS,
  DUTY_CHANGE_TASK_KEYS,
  FLEX_NOTIFICATION_TASK_KEYS,
  TASK_QUERY_CACHE_TIME
} from '../constants';
import { usePlannerMessagesStore } from '../store';

type TabCounts = Record<PlannerTaskTab, number> & {
  leaveSeenCount: number;
};

// Open only — the badges say what still needs action, so the "show history"
// toggle widens the grid without changing them.
const useGetTabCounts = () => {
  const costCenterIds = usePlannerMessagesStore((s) => s.costCenterIds);
  const startDate = usePlannerMessagesStore((s) => s.startDate);
  const endDate = usePlannerMessagesStore((s) => s.endDate);

  // Same instants getPlannerTasks sends, so the badges and the grid put the same
  // bounds on create_time_ instead of drifting apart by the server/browser offset.
  const query = queries.useGetTaskDefinitionsCount(
    USERNAME_PLACEHOLDER,
    costCenterIds.join(','),
    startDate ? getStartOfDay(startDate).toISOString() : undefined,
    endDate ? getEndOfDay(endDate).toISOString() : undefined,
    true,
    {
      placeholderData: [],
      keepPreviousData: true,
      cacheTime: TASK_QUERY_CACHE_TIME
    }
  );

  // Known asymmetry with the grid's own total: the count queries semi-join the
  // candidate identity links (EXISTS) while selectInboxTasks INNER JOINs them, so
  // a task carrying two candidate links for this user is one here and two rows in
  // the grid. Same for a process instance with two in-scope brId variables. If a
  // badge and the grid's Content-Range disagree by a handful, that is why.
  const counts = query.data ?? [];
  const countByKey = new Map(counts.map((c) => [c.definitionKey, c.count]));
  const sum = (keys: readonly string[]) =>
    keys.reduce((total, key) => total + (countByKey.get(key) ?? 0), 0);

  const tabCounts: TabCounts = {
    [PlannerTaskTab.ALL]: counts.reduce((total, c) => total + c.count, 0),
    [PlannerTaskTab.LEAVE_REQUESTS]: sum(LEAVE_UNREAD_TASK_KEYS),
    [PlannerTaskTab.DUTY_CHANGES]: sum(DUTY_CHANGE_TASK_KEYS),
    [PlannerTaskTab.FLEX_NOTIFICATIONS]: sum(FLEX_NOTIFICATION_TASK_KEYS),
    leaveSeenCount: sum(LEAVE_SEEN_TASK_KEYS)
  };

  return { ...query, data: tabCounts };
};

export default useGetTabCounts;
